import { useState } from "react";
import { motion } from "framer-motion";
import { Activity, AlertTriangle } from "lucide-react";
import { api, type JobStatus } from "../api/client";
import JobProgressIndicator from "../components/JobProgressIndicator";

const DATA_TYPES = [
  { v: "microbiome", l: "Microbiome" },
  { v: "genomic", l: "Genomic Variant" },
  { v: "proteomic", l: "Proteomic" },
  { v: "metabolomic", l: "Metabolomic" },
  { v: "clinical_labs", l: "Clinical Labs" },
];

export default function InterpretPage() {
  const [dataType, setDataType] = useState("clinical_labs");
  const [audience, setAudience] = useState("clinician");
  const [dataJson, setDataJson] = useState(
    '[\n  {\n    "name": "Haemoglobin",\n    "value": 115,\n    "unit": "g/L",\n    "reference_range": "120-150"\n  }\n]'
  );
  const [jobStatus, setJobStatus] = useState<JobStatus | "idle">("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [interpretation, setInterpretation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    let data_points: Array<Record<string, unknown>>;
    try {
      data_points = JSON.parse(dataJson);
      if (!Array.isArray(data_points) || data_points.length === 0) {
        throw new Error("Data points must be a non-empty array");
      }
    } catch (e) {
      setError(`Invalid JSON: ${e instanceof Error ? e.message : "parse error"}`);
      return;
    }
    setJobStatus("pending");
    setElapsedMs(0);
    setError(null);
    setInterpretation(null);
    try {
      const res = await api.interpret(
        { data_type: dataType, data_points, audience },
        (p) => {
          setJobStatus(p.status);
          setElapsedMs(p.elapsed_ms);
        }
      );
      setInterpretation(res.interpretation);
      setJobStatus("completed");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
      setJobStatus("failed");
    }
  };

  const isWorking = jobStatus === "pending" || jobStatus === "running";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Activity className="text-nhs-darkblue" /> Data Interpretation
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Interpret biomarker, genomic, and clinical lab data with UK clinical context.
        </p>
      </div>

      <div className="card">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label" htmlFor="data-type">Data type</label>
            <select
              id="data-type"
              className="input"
              value={dataType}
              onChange={(e) => setDataType(e.target.value)}
              disabled={isWorking}
            >
              {DATA_TYPES.map((d) => (
                <option key={d.v} value={d.v}>
                  {d.l}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="audience">Audience</label>
            <select
              id="audience"
              className="input"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              disabled={isWorking}
            >
              <option value="clinician">Clinician</option>
              <option value="consumer">Consumer (plain English)</option>
            </select>
          </div>
        </div>
        <label className="label" htmlFor="data-points">Data points (JSON array)</label>
        <textarea
          id="data-points"
          rows={10}
          className="input font-mono text-sm"
          value={dataJson}
          onChange={(e) => setDataJson(e.target.value)}
          disabled={isWorking}
        />
        <button onClick={submit} disabled={isWorking} className="btn-primary mt-3 w-full md:w-auto">
          {isWorking ? "Interpreting..." : "Interpret Data"}
        </button>
      </div>

      <JobProgressIndicator
        status={jobStatus}
        elapsedMs={elapsedMs}
        label="Interpreting your data..."
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded flex gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {interpretation && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card">
          <h2 className="font-semibold mb-3">Interpretation</h2>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-800">
            {interpretation}
          </div>
          <p className="text-xs text-gray-500 italic mt-4 pt-3 border-t">
            AI-generated. Not a clinical diagnosis. Must be reviewed by a qualified healthcare
            professional.
          </p>
        </motion.div>
      )}
    </div>
  );
}
